const format = `# Reichhaltige multimediale Artikel als Standardformat

## Topicles

Topicles sind der JSON-Standardcontainer für das dpa-digitalwires-Format.
In diesen Containern werden alle Nachrichteninhalte der dpa-Gruppe transportiert.
Dazu gehören:

* dpa-Textfunk
* dpa-Bildfunk
* dpa-Weblines
* Multimediale Dienste der dpa

Das Kernelement des dpa-digitalwires-Formats ist das sogenannte **Topicle**.
Topicles vereinen Aspekte von multimedialen Artikeln (**Articles**) auf der einen
und Nachrichtenpaketen (**Topics**) auf der anderen Seite.

Topicles können damit alle Inhalte der dpa als ein kohärentes, in sich abgeschlossenes,
JSON-Objekt repräsentieren. Dazu gehören:

* Reine Text-Artikel aus dem dpa-Textfunk
* Einzelne Bilder aus dem dpa-Bildfunk
* Multimediale Artikel bestehend aus Text und direkt verknüpften Bildern/Assets, z.B. aus den Weblines und den multimedialen Diensten der dpa
* Pakete aus mehreren Texten, Bildern und weiteren Assets z.B. von dpa-news

Das dpa-digitalwires-Format orientiert sich an den Anforderungen sowohl der
redaktionellen Nutzung (z.B. durch Weiterverarbeitung in einem CMS) als auch
der automatisierten Veröffentlichung dafür lizensierter Inhalte (z.B. der Weblines).

In den Bereitstellungsverfahren der dpa-digitalwires (siehe <Navlink exact to="/doc/apis">Dokumentation | APIs</NavLink>)
werden Topicles entweder einzeln oder im Rahmen eines Feeds mit mehreren Topicles
bereitgestellt. 

Das Feedformat ist bei jenen Bereitstellungsverfahren dokumentiert, die Feeds anbieten.

### Hinweis zu weiteren Agenturen

Neben den von der dpa erstellten Inhalten werden auch von der dpa verbreitete Agenturen wie Associated Press (AP) und Europa Press via dpa-digitalwires bereitgestellt.

Für Pilotkunden stehen auch die Dienste der Katholischen Nachrichten-Agentur (KNA) via dpa-digitalwires zur Verfügung.

Sollten Sie an Inhalten weiterer Agenturen via dpa-digitalwires interessiert
sein, kontaktieren Sie uns gerne per E-Mail
<a href="mailto:newslab-support@dpa.com">newslab-support@dpa.com</a>.

### Hinweise zur Weiterentwicklung des Formats

Das dpa-digitalwires-Format wird regelmäßig abwärtskompatibel erweitert, z.B.
wenn weitere Inhalte der dpa-Gruppe via dpa-digitalwires bereitgestellt werden
können.

Aktuell werden mit der Version 0.8.x weitere Metadaten im Text- und Bildfunk zur
Verfügung gestellt, die die Ablösung dieser Dienste in Systemen, die bislang
das NewsML-G2 Format erhalten ermöglichen. Dies steht im Zusammenhang mit einer
für 2024 geplanten Umstellung des dpa-Basisdienstes auf einen multimedialen
Dienst, der als solcher nur noch via dpa-digitalwires zur Verfügung stehen wird.

### Überblick

Im Weiteren werden hier die Bestandteile des Topicle-Formats vorgestellt:

- <NavLink exact to="/doc/format#der-über-alle-inhaltetypen-hinweg-gemeinsame-kern">Der über alle Inhaltetypen hinweg gemeinsame Kern</NavLink>
- <Navlink exact to="/doc/format#erweiterung-um-storytelling-elemente">Erweiterung um Storytelling-Elemente</NavLink>
- <Navlink exact to="/doc/format#spezifische-erweiterungen-und-anpassungen">Spezifische Erweiterungen und Anpassungen, z.B. für dpa-Text- und dpa-Bildfunk, dpa-Weblines, multimediale Dienste und Nachrichtenpakete</NavLink>

## Der über alle Inhaltetypen hinweg gemeinsame Kern

Strukturell gliedert sich ein Topicle in vier Teile:

* [Essentielle Metadaten](#essentielle-metadaten)
* Das [Hauptobjekt](#hauptobjekt-des-topicle-nachrichtentext), oft ein Nachrichtentext
* Optional mit dem Hauptobjekt [assoziierte Bilder oder sonstige Medienobjekte](#assoziierte-medienobjekte)
* [Kategorische Metadaten](#kategorische-metadaten)

<a href="/img/topicle-dw.png" target="_blank"><img src="/img/topicle-dw.png" width=100%></a>

Im Folgenden werden diese vier Teile kurz beschrieben.

### Essentielle Metadaten

Essentielle Metadaten werden verwendet, um Topicles zu aktualisieren und zu verwalten.
Sie enthalten folgende Keys:

- **format**: Das Format des Entries. Der Name kombiniert mit einer Versionsnummer entlang der Richtlinien des Semantic Versioning (z.B. digitalwires@0.8.0).
- **entry_id**: Die technische ID des Eintrages im dpa-digitalwires-Format. Ein opaker Hash.
- **updated**: Der Zeitstempel der Aktualisierung des Entries, Zeitzone UTC ("Zuluzeit").
- **urn**: Die URN des Hauptobjektes des Topicles.
- **version_created**: Der Zeitstempel der letzten Version des Hauptobjektes des Topicles mit Zeitzone, bei dpa in der Regel Europe/Berlin.
- **version**: Die Version des Hauptobjektes des Topicles. Bei dpa Inhalten ein Integer.
- **type**: Der Typ des Topicles. Aktuell immer **composite**.
- **pubstatus**: Status der Veröffentlichung. Einer von drei Werten: "usable", "withheld", "canceled".
- **signal**: Weitere Hinweise zum Anlass (z.B. Berichtigung/Aktualisierung) für die Veröffentlichung. Entspricht dem <a href="https://cv.iptc.org/newscodes/signal/" target="_blank">G2-Signal</a>.
- **embargoed**: Der Zeitstempel einer einzuhaltenden Sperrfrist mit Zeitzone analog zu **version_created**.
- **language**: Die Sprache des Topicles.
- **urgency**: Die Dringlichkeit der Meldung. Ein Integer zwischen 1 und 9. Entspricht dem gleichnamigen Element in der IPTC News Architecture. Bei dpa-Meldungen werden die Werte 1 = Blitzmeldung, 2 = Eilmeldung bis 6 verwendet. Andere Nachrichtenagenturen verwenden andere Skalen.

### Hinweise

#### **null** als Wert

Der Wert \`null\` ist für alle Keys zulässig, falls die entsprechende Information nicht existiert.

#### Zusammenhang zwischen **entry_id** und **urn**
* Die **entry_id** ist eine low-level ID, die für die technische Übertragung relevant ist. Anhand der **entry_id** können Sie überprüfen, ob Sie genau dieses Topicle schon einmal erhalten haben. Eine doppelte Übertragung kann in Ausnahmefällen auftreten, z.B. aufgrund des asynchronen und verteilten Backends, das das dpa-digitalwires-Format generiert.
* Der **urn**, d.h. der <a href="https://de.wikipedia.org/wiki/Uniform_Resource_Name" target="_blank">Uniform Resource Name</a> ist die stabile semantische ID des Hauptobjektes des Topicles.

Topicles mit unterschiedlicher **urn** sind im Redaktionssystem der dpa
unterschiedliche Objekte, obwohl sie eventuell sehr ähnliche Texte transportieren. 
Weblines-Meldungen basieren in der Regel auf Meldungen des Textfunkes, haben aber
eine andere **urn**, weil z.B. der Text verändert oder Text-/Bildverknüpfungen
hinzugefügt wurden.

Bei Text- und Bildfunkdiensten verweist der Key \`urn\` auf den **urn** des jeweiligen Textes respektive Bildes. Bei Feature- und Weblines-Meldungen handelt es sich um den **urn**
des Textes der multimedialen Story. Bei Paketen hingegen wird hier die **urn** des Nachrichtenpaketes transportiert.

#### Zusammenhang zwischen den Zeitstempeln **updated** und **version_created**

Ähnlich wie mit **entry_id** und **urn** verhält es sich auch mit den beiden
Zeitstempeln **updated** und **version_created**.

- **updated** gibt an, wann das Topicle das letzte Mal aktualisiert wurde und ist immer in ISO8601 "Zuluzeit" (Zeitzone UTC) angegeben.
- **version_created** bildet ab, wann die letzte semantisch relevante Version des Hauptobjektes des Topicles erzeugt wurde. **version_created** kann z.B. auf Artikelseiten angezeigt werden, daher erfolgt die Darstellung in ISO8601 mit Zeitzoneninformation. Die Zeitangaben sind dabei bei dpa-Inhalten immer in der Zeitzone von Berlin.

#### Topicle-Status erkennen mittels **pubstatus**, **embargoed** und **signal**

Wichtige Hinweise zum Publikationsstatus eines Topicles ergeben sich aus drei Werten:
- **pubstatus**: Zeigt an, ob das Topicle grundsätzlich verwendbar (\`usable\`) oder zurückgezogen (\`canceled\`) ist und daher depubliziert werden sollte.
- **embargoed**: Ein Zeitstempel hier bedeutet, dass bis zum angegebenen Zeitpunkt eine Sperrfrist einzuhalten ist und das Topicle nicht publiziert werden darf.
- **signal**: Signalisierung verschiedenster Eigenschaften der Änderungen am Inhalt durch die Redaktion. Bei der dpa werden aktuell die Signale Aktualisierung (\`sig:update\`), Wiederholung (\`sig:repeat\`) und Berichtigung (\`sig:correction\`) gesendet. Andere Nachrichtenagenturen senden ggf. andere Signale.

### Hauptobjekt des Topicle: Nachrichtentext

In den allermeisten Fällen ist das Hauptobjekt eines Topicle der Text des Artikels/der multimedialen Story.
Verwendet werden die folgenden Keys bzw. Attribute:

  - **kicker**: Die Dachzeile des Artikels. Ein String.
  - **headline**: Die Hauptüberschrift des Artikels. Ein String.
  - **subhead**: Die Unterzeile des Artikels. Ein String.
  - **teaser**: Der Teaser des Artikels. Ein String
  - **byline**: Die Autorenzeile des Artikels. Ein String.
  - **dateline**: Die Ortszeile des Artikels. Ein String.
  - **article_html**: Der eigentliche Artikeltext als HTML \`<section>\`.
  - **infobox_html**: Die zum Artikel gehörende Infobox als HTML \`<section>\`.
  - **linkbox_html**: Externe Links, die gewöhnlicherweise unterhalb des Artikeltextes angezeigt werden, als HTML \`<section>\`.
  - **creditline**: Die Agenturzeile / Urheberzeile des Artikels. Ein String.
  - **copyrightnotice**: Der Copyright-Hinweis für den Artikel. Ein String.
  - **usageterms**: Die urheberrechtlichen Nutzungsbedingungen des Artikels. Ein String.
  - **autopublishnotice**: Der Urheberrechtshinweis für Kunden die Consumer-Ready-Inhalte wie z.B. die dpa-Weblines automatisiert publizieren. Sollte auf der Detailseite des Artikels angezeigt werden. Ein String.
  - **ednotes**: Redaktionelle Hinweise zum Artikel. Eine Liste von Objekten.
  - **descriptions**: Liste von Beschreibungen. Derzeit v.a. in Bilderdiensten für verschiedene Bildunterschriftvarianten (unterschieden durch den Wert des Keys \`role\`). Die eigentlichen Texte werden jeweils im Key \`description\` angegeben.

#### Hinweise

##### **article_html**, **infobox_html**, **linkbox_html**

Im Wert des Attributs **article_html** befindet sich der Artikel in HTML5-Repräsentation. Dort werden aktuell in dpa-Meldungen die folgenden HTML-Tags verwendet:
- **section**,**p**,**h2**,**strong**,**em** für Gliederung, Zwischenüberschriften, Fettungen und kursive Textauszeichnung.
- **a** für externe und interne Links.
- **ul**,**ol**, **li** für Bulletpointlisten und nummerierte Listen.
- **table**, **tbody**, **tr**, **td** für Tabellen.

Bei anderen Agenturen/Diensten können weitere HTML5-Tags verwendet werden. Bei reinen Bilderdiensten bleibt \`article_html\` ggf. leer.

##### Links in **article_html**, **infobox_html**, **linkbox_html**

- Externe Links im Artikelinhalt werden als **\`<a class="externalLink" href="...">...</a>\`** ausgezeichnet.
- Interne Links (d.h. Links auf andere Artikel / Topicles) im Artikeltext werden als **\`<a class="internalLink" data-uri="urn:newsml:dpa.com:20090101:xx-yy-zzz" href="">\`** transportiert. D.h. die URN des verlinkten Topicles wird im Attribut **data-uri** übermittelt.

##### Escaping von Unicode-Zeichen

Wie das Beispiel zeigt, werden Unicode-Zeichen escaped übermittelt. Damit ist die gesamte JSON-Response "ASCII-encoded" und somit auf maximale Kompatibilität mit Zielsystemen ausgelegt. Diese Art von Encoding und Escaping sollte automatisch von Ihrer Software verarbeitet werden können.

\`\`\`javascript
{
  "article_html": "<section class=\\"main\\">
<p>Besser ist es, wenn Kinder die Bedeutung der W\\u00f6rter selbst erkunden, ... </p>
<p>Zum \\u00dcben bieten sich kleine Momente des Alltags an. Etwa ... Stern.</p>
</section>"
}
\`\`\`

#### Redaktionelle Hinweise: **ednotes**

Die ednotes-Liste enthält Hinweise der Nachrichtenagentur an die Redaktion des Empfängers / Kunden. Diese Hinweise können ggf. eine weitere Verfeinerung über ihre Art durch den Wert des Keys \`role\` erfahren.

Die Standardstruktur dieser Objekte ist wie folgt:

- **role**: Die Rolle des Hinweises. Ein Qcode.
- **ednote**: Der Hinweistext. Ein String.
- **is_publishable**: Gibt an, ob der Hinweis nach Abwägung durch die empfangende Redaktion veröffentlicht werden kann.

In dpa-Diensten werden aktuell folgende Hinweis-Rollen (ggf. auch mehrfach) verwendet:

- **dpaednoterole:closingline**: Die "traditionelle" Schlusszeile am Ende eines Artikels aus dem IPTC7901 Format. Hier noch aus Gründen der Abwärtskompatibilität enthalten.
- **dpaednoterole:contacts**: Ansprechpartner einer in der Meldung angesprochenen Einrichtung.
- **dpaednoterole:correction**: Hinweise zu Grund und Form einer Korrektur oder Zurückziehung.
- **dpaednoterole:correctionshort**: Kurzfassung der *dpaednoterole:correction*.
- **dpaednoterole:correction_consumerready**: Publizierbarer, leserfreundlicher Korrekturhinweis in Consumer-Ready-Diensten.
- **dpaednoterole:dpacontacts**: Kontaktperson(en) in der dpa-Redaktion zu dieser Meldung.
- **dpaednoterole:editorialnote**: Allgemeine Hinweise an die empfangende Redaktion.
- **dpaednoterole:edservice**: Ergänzender Service-Hinweis zum Meldungsinhalt.
- **dpaednoterole:genrenote**: Hinweis im Zusammenhang mit dem Genre der Meldung.
- **dpaednoterole:picture**: Bild-/Grafik-Hinweis.
- **dpaednoterole:embargo**: Hinweis zu einem Embargo.

Inhalte, insbesondere von anderen Agenturen, können andere/weitere redaktionelle Hinweise enthalten.

### Assoziierte Medienobjekte

Assoziierte Medienobjekte können Bilder, Grafiken, Audios, Videos oder Texte sein,
die im Zusammenhang mit dem Hauptobjekt des Topicles stehen. Innerhalb des
Topicle-JSON werden die textuellen Informationen und Metadaten dieser Objekte
transportiert, die eigentlichen Binärobjekte sind in der Regel verlinkt.

Aktuell sind die assoziierten Medienobjekte in der überwiegenden Mehrzahl Bilder.
Assoziierte Medienobjekte werden als Liste von **associations** transportiert.
Diese haben mindestens die Keys: **urn** und **type**.

#### Grundlagen

Ein Objekt in der Liste der **associations** besteht aus zwei Teilen:

- Grundlegende Metadaten, darunter:
  - **urn**: Die stabile semantische ID des Medienobjektes.
  - **version**: Die Version des Medienobjektes. Bei dpa Inhalten ein Integer.
  - **version_created**: Der Zeitstempel der letzten Version des Medienobjektes mit Zeitzone, bei dpa in der Regel Europe/Berlin.
- **renditions**: Liste mit dem konkreten Objekt in einem oder mehreren Medienformaten. Vorhanden sind jeweils mindestens:
  - **url**: Hier wird auf das Binärobjekt des Medienobjektes via URL verwiesen. Diese URL ist signiert und hat ein Ablaufdatum von ca. 2 Tagen. Dies sollte ausreichen, um das Medienobjekt in das eigene CMS zu importieren.
  - **mimetype**: Der Mimetype des Medienobjektes.

**Hinweis zum Abruf:** Vor allem bei größeren Medienobjekten kann bei Abfrage der **url** gelegentlich ein HTTP Status Code 404 zurückgegeben werden. Es wird ein \`retry-after\`-Header zurückgegeben, der die Zeitspanne in Sekunden enthält, nach der das Medienobjekt für eine erneute Abfrage bereitsteht. Eine Referenzimplementierung bieten wir unseren Kunden auf <a href="https://github.com/dpa-newslab/dpa-digitalwires-snippets/tree/main/association-requests" target="_blank">Github als open-source</a> an.

#### Assoziierte Bilder

Für jedes Bild gibt es die folgenden Keys:

  - **type**: Der Typ des Medienobjektes. Hier \`image\`.
  - **urn**: Der URN des Bildes.
  - **rank**: Der Rang des Bildes in der Reihenfolge aller assoziierten Bilder. Ein Integer.
  - **version**: Die Version des Bildes. Bei dpa-Inhalten ein Integer.
  - **version_created**: Der Zeitstempel der letzten Version des Bildes mit Zeitzone, bei dpa in der Regel Europe/Berlin.
  - **is_featureimage**: Ein Indikator, der angibt, ob das Bild das Feature/Heroimage des Artikels ist. Wird aktuell automatisiert für das erste Bild vergeben und ist dann \`true\`, sonst \`false\`.
  - **headline**: Der Titel des Bildes.
  - **caption**: Die Bildunterschrift.
  - **creditline**: Die Creditline des Bildes. Ein String im Format "Fo­to­graf / Urheber" oder "Fo­to­graf / Quelle / Verbreiter" (bei Bildern, die von Fremdagenturen bezogen wurden). Z.T auch über mehrere Verbreiter. Muss bei Verwendung des Bildes in dessen Kontext angezeigt werden.
  - **renditions**: Eine Liste von Objekten mit den konkreten Bildformaten für das Bild.
    - **url**: Hier wird auf das Binärobjekt des Bildes via URL verwiesen. Diese URL ist signiert und hat ein Ablaufdatum von ca. 2 Tagen. Dies sollte ausreichen, um das Bild in das eigene CMS zu importieren.
    - **mimetype**: Der Mimetype des Bildes.
    - **width**: Die Breite des Bildes in Pixeln.
    - **height**: Die Höhe des Bildes in Pixeln.
    - **size**: Die Länge der längeren Kante in Pixeln.
    - **fovealarea**: Bildschwerpunkt (mit den Keys \`left\`, \`right\`, \`top\`, \`bottom\` jeweils von der linken oberen Ecke gemessen).

##### Hinweise

- Standardmäßig werden die Bilder mit einer längeren Kante von \`2048px\` ausgeliefert.
  - andere Kantenlängen sind auf Anfrage und ggf. gegen Aufpreis möglich.
- Einige Bilder mit zusätzlichen Metadaten zum sog. "Bildschwerpunkt" (z.B. in den Weblines) können zusätzlich auch pixelgenau skaliert, z.B. im Format \`1920x1080px\`, ausgeliefert werden.

#### Beispielmeldungen

- <NavLink exact to="/doc/format#bildfunk-bild">Bildfunk Bild</NavLink>

#### Assoziierte Grafiken, Audios und Videos

Die monomedialen Video-, Audio- und Grafikdienste der dpa stehen ebenfalls im dpa-digitalwires-Format zur Verfügung. Diese Medientypen sind aktuell noch nicht direkt mit einer multimedialen dpa-Meldung verknüpft.

#### Assoziierte Grafiken

Für jede Grafik gibt es die folgenden Keys:

  - **type**: Der Typ des Medienobjektes. Hier \`graphic\`.
  - **urn**: Der URN der Grafik.
  - **rank**: Der Rang der Grafik in der Reihenfolge aller assoziierten Grafiken. Ein Integer.
  - **version**: Die Version der Grafik. Bei dpa-Inhalten ein Integer.
  - **version_created**: Der Zeitstempel der letzten Version der Grafik mit Zeitzone, bei dpa in der Regel Europe/Berlin.
  - **headline**: Der Titel der Grafik.
  - **caption**: Die Grafikunterschrift.
  - **renditions**: Eine Liste von Objekten für die Grafik und das Preview.
    - **url**: Hier wird auf das Binärobjekt der Grafik via URL verwiesen. Diese URL ist signiert und hat ein Ablaufdatum von ca. 2 Tagen. Dies sollte ausreichen, um die Grafik in das eigene CMS zu importieren.
    - **role**: Die Rolle der Grafik. Mögliche Werte: \`vector\`, \`preview\`.
    - **mimetype**: Der Mimetype der Grafik.
    - **width**: Die Breite der Grafik in Pixeln.
    - **height**: Die Höhe der Grafik in Pixeln.
    - **size**: Die Länge der längeren Kante in Pixeln.

#### Beispielmeldungen

- <NavLink exact to="/doc/format#grafik">Grafik</NavLink>

#### Assoziierte Audios

Für jedes Audio gibt es die folgenden Keys:

  - **type**: Der Typ des Medienobjektes. Hier \`audio\`.
  - **urn**: Der URN des Audios.
  - **rank**: Der Rang des Audios in der Reihenfolge aller assoziierten Audios. Ein Integer.
  - **version**: Die Version des Audios. Bei dpa-Inhalten ein Integer.
  - **version_created**: Der Zeitstempel der letzten Version des Audios mit Zeitzone, bei dpa in der Regel Europe/Berlin.
  - **headline**: Der Titel des Audios.
  - **caption**: Die Audiounterschrift.
  - **renditions**: Eine Liste von Objekten mit konkreten Audioformaten für das Audio.
    - **url**: Hier wird auf das Binärobjekt des Audios via URL verwiesen. Diese URL ist signiert und hat ein Ablaufdatum von ca. 2 Tagen. Dies sollte ausreichen, um das Audio in das eigene CMS zu importieren.
    - **role**: Die Rolle des Audios. Mögliche Werte: \`raw\`.
    - **mimetype**: Der Mimetype des Audios.
    - **audiobitrate**: Die Bitrate des Audios in Kbit/s (kbps).
    - **audiosamplerate**: Die Samplerate des Audios in Hz.

#### Beispielmeldungen

- <NavLink exact to="/doc/format#audio">Audio</NavLink>

#### Assoziierte Videos

Für jedes Video gibt es die folgenden Keys:

  - **type**: Der Typ des Medienobjektes. Hier \`video\`.
  - **urn**: Der URN des Videos.
  - **rank**: Der Rang des Videos in der Reihenfolge aller assoziierten Videos. Ein Integer.
  - **version**: Die Version des Videos. Bei dpa-Inhalten ein Integer.
  - **version_created**: Der Zeitstempel der letzten Version des Videos mit Zeitzone, bei dpa in der Regel Europe/Berlin.
  - **headline**: Ein kurzer Videotitel.
  - **caption**: Die Videounterschrift.
  - **transcript**: Das Transkript des Videos (optional).
  - **renditions**: Eine Liste von Objekten für das Video und das Preview.
    - **url**: Hier wird auf das Binärobjekt des Videos via URL verwiesen. Diese URL ist signiert und hat ein Ablaufdatum von ca. 2 Tagen. Dies sollte ausreichen, um das Video in das eigene CMS zu importieren.
    - **role**: Die Rolle des Videos. Mögliche Werte: \`poster\` (Vorschaubild), \`raw\` (Video) und \`captions\` (Untertitel).
    - **mimetype**: Der Mimetype mimetype der Rendition.
    - **width**: Die Breite der Rendition in Pixeln.
    - **height**: Die Höhe der Rendition in Pixeln.
    - **size**: Die Länge der längeren Kante in Pixeln.

#### Beispielmeldungen

- <NavLink exact to="/doc/format#video">Video</NavLink>

### Kategorische Metadaten

Kategorische Metadaten ordnen den Artikel in verschiedene Kategorien
ein und liefern darüberhinaus weitere Informationen. Sie werden als Liste von Objekten
unter dem Key **categories** transportiert.

Die Standardstruktur dieser Objekte ist wie folgt:

- **name**: Der Displayname der Kategorie.
- **type**: Der Typ der Kategorie als **dnltype:xxxx**.
- **qcode**: Ein QCode der Kategorie in der Form **Kategorieprefix:ID**. Er findet im Wesentlichen bei Typen mit "Closed Vocabularies" Verwendung.
- **rank**: Der Rang der Kategorie in Bezug auf den jeweiligen Typ.

Neben dem verpflichtenden \`type\` sind \`name\` und \`qcode\` optional. Mindestens einer der beiden Keys muss aber einen Wert enthalten.

Weitere Keys sind abhängig vom Typ der Kategorie möglich (z.B. \`role\`).

#### Hinweise zu QCodes

QCodes haben ihren Urspung in der IPTC News Architecture und sind im Wesentlichen identisch zu den CURIEs des W3C. Sie bestehen aus zwei Teilen: Dem Kategoriepräfix (oder Namespace), der das zugrundeliegende Vokabular definiert, und der eigentlichen ID, welche die ID eines Wertes in diesem Vokabular festlegt.

Die IPTC hat eine <a href="https://www.iptc.org/standards/newscodes" target="_blank">Vielzahl von Vokabularen</a> definiert, die hier zum Teil eingesetzt werden. Die Nachrichtenagenturen selbst definieren häufig zusätzliche eigene Vokabulare, die dann eigene Namespaces erhalten. Für die Wichtigsten dieser Vokabulare bieten wir hier CSV Dateien mit den ID-Werten zum Download an.

Ein QCode ist eine stabile und in der Regel auch sprachübergreifend einheitliche ID. Daher sollte, wenn vorhanden, auf Empfängerseite der \`qcode\` für Filter- und Routingzwecke o.Ä. verwendet werden und nicht der \`name\`.

### Kategorische Metadaten im aktuellen Formatkern

Es folgt eine Auflistung der kategorischen Metadaten, die aktuell im Formatkern definiert sind.

- **dnltype:dpasubject**: Ein dpa-Erststichwort bzw. ein Mediatopic der IPTC. Ein "Closed Vocabulary" von ca. 400 Begriffen.
  - [Aktuelle Version der dpa-Erststichwörter als CSV Datei](/res/qcodes_dpasubject_de.csv)
  - [Aktuelle Version der IPTC-Mediatopics als CSV Datei](/res/qcodes_medtop_de.csv)
- **dnltype:desk**: Ein dpa-Ressort. Ein "Closed Vocabulary" von 6 Begriffen.
    - [Aktuelle Version der dpa-Ressorts als CSV Datei](/res/qcodes_dpacat_de.csv)
- **dnltype:urgency**: Priorität des Entries. Ist identisch mit Key \`urgency\` des Topicles und wird hier aus Vereinheitlichungsgründen auch als Kategorie geführt.
- **dnltype:signal**: Strukturierte Hinweise auf Besonderheiten des Entries, die ggf. besondere Aufmerksamkeit erfordern (vgl. <Navlink exact to="/doc/format#topicle-status-erkennen-mittels-pubstatus-embargoed-und-signal">Topicle-Status erkennen mittels pubstatus, embargoed und signal</NavLink>).
- **dnltype:pubstatus**: Status der Veröffentlichung.
- **dnltype:wire**: Eine Dienstekennung. Ein Topicle enthält immer alle Dienste, an die es gesendet wurde. Das kann bedeuten, dass Dienste auftauchen, die Sie nicht abonniert haben. Bei dpa-Inhalten stammt das \`dnltype:wire\` aus einem "Closed Vocabulary" von ca. 30 Begriffen.
  - [Aktuelle Version der dpa-Dienstekennung als CSV Datei](/res/qcodes_wire.csv)
- **dnltype:rubric**: Eine dpa-Weblines respektive dpa-News-Rubrik. Ein "Closed Vocabulary" von ca. 400 Begriffen.
  - [Aktuelle Version der dpa-Rubriken als CSV Datei](/res/qcodes_rubric.csv)
- **dnltype:wikidata**: Für den Nachrichteninhalt relevante Wikidata-Konzepte. Mit \`wikidata:\`-QCode.
- **dnltype:subject**: Ein Stichwort aus einem "Closed Vocabulary" (z.B. IPTC-Mediatopics, Stichwörter anderer Agenturen).
- **dnltype:geosubject**: Ein Geostichwort. Bei dpa-Meldungen aus den folgenden Listen von QCodes:
  - [Aktuelle Version der dpa-Geostichwörter (Städte) als CSV Datei](/res/qcodes_dpageosubject_de.csv)
  - [Aktuelle Version der dpa-Geostichwörter (Länder) als CSV Datei](/res/qcodes_dpacountry_de.csv)
- **dnltype:genre**: Journalistisches Genre des Nachrichteninhalts. Verwendete geschlossene Vokabulare abhängig von Typ und Quelle des Inhalts.
  - [Aktuelle Version der dpa-Genre als CSV Datei](/res/qcodes_dpatextgenre_de.csv)
- **dnltype:provider**: Verbreiter des Inhalts.
- **dnltype:organization**: Für den Nachrichteninhalt relevante Organisationen. Ggf. mit \`wikidata:\`-QCode.
- **dnltype:person**: Für den Nachrichteninhalt relevante Personen. Ggf. mit \`wikidata\`-QCode.
- **dnltype:creator**: Ersteller des Entries.
- **dnltype:contributor**: Weitere Redaktionsmitglieder, die am Entry mitgewirkt haben.
- **dnltype:infosource**: Quellen des Inhaltes (insbes. bei Bildern), ggf. aufgeteilt in Quelle und Verbreiter (z.B. bei der Übernahme eines Bildes von einer Fremdagentur oder des Handouts eines Autoherstellers etc.).
- **dnltype:keyword**: Ein Zweitstichwort. Ein String.
- **dnltype:poi**: Point-of-Interest zum Nachrichteninhalt.
- **dnltype:locationcreated**: Aufnahmeort eines Nachrichteninhalts (v.a. bei Bildern).

#### Typspezifische Erweiterungen

Bei Bedarf werden bei bestimmten Typen von Kategorien typspezifische Erweiterungen um weitere
Keys vorgenommen. 

Aktuell gilt das im Kern für die folgenden Typen:
- Bei **dnltype:infosource**, **dnltype:creator** und **dnltype:contributor** kann es zusätzlich eine **role** geben, die die Funktion näher spezifiziert. 
- Bei **dnltype:poi** wird unter dem Key **geojson** bei Orten in der Meldung ein GeoJson-Feature-Objekt zurückgeliefert. Aktuell mit einem GeoJson Point als Wert des **geometry**-Keys und mindestens der Keys **name** und **formatted_address** als Attribute des **properties**-Keys.

Weitere mögliche Keys sind: **country** für das Land, **locality** für die Gemeinde und **administrative_area_level_1** für das Bundesland (respektive im Ausland für die entsprechende 1. Administrative Ebene unterhalb der Landesebene).

Ein Beispiel:

\`\`\`javascript
"categories": [
  {
   "type": "dnltype:poi", 
   "name": "Bundesanwaltschaft, Brauerstra\u00dfe 30, 76135 Karlsruhe, Deutschland", 
   "qcode": null, 
   "geojson": {
    "geometry": {
     "type": "Point", 
     "coordinates": [
      8.3849201, 
      49.00189
     ]
    }, 
    "type": "Feature", 
    "properties": {
     "country": "Deutschland", 
     "formatted_address": "Brauerstra\u00dfe 30, 76135 Karlsruhe, Deutschland", 
     "name": "Bundesanwaltschaft", 
     "administrative_area_level_1": "Baden-W\u00fcrttemberg", 
     "locality": "Karlsruhe"
    }
   }
  },
  // next category...
]
\`\`\`

- Bei **dnltype:locationcreated** wird unter dem Key **geojson** ein GeoJson-Feature-Objekt zurückgeliefert, das den Aufnahmeort eines Bildes oder anderer Nachrichteninhalte beinhaltet. Attribute des **properties**-Keys sind dabei meist **locality** für die Gemeinde, **administrative_area_level_1** für das Bundesland (respektive im Ausland für die entsprechende 1. Administrative Ebene unterhalb der Landesebene) sowie **country** für das Land. Einen **geometry**-Key mit genauen Koordinaten gibt es in vielen Fällen nicht.

Ein Beispiel:
\`\`\`
"categories": [
  {
   "type": "dnltype:locationcreated", 
   "name": "Herzogenaurach", 
   "qcode": null, 
   "geojson": {
    "type": "Feature", 
    "properties": {
      "locality": "Herzogenaurach",
      "administrative_area_level_1": "Bayern",
      "country": "Deutschland"
    }
   }
  },
  // next category...
]
\`\`\`

## Erweiterung um Storytelling-Elemente

Multimediale Stories können um Storytelling-Elemente erweitert werden. Akuell
geschieht das in den dpa-Weblines, sowie den dpa-Themenwelten und den
dpa-Faktenchecks.

Im Wesentlichen wird bei diesen Erweitungen (im Unterschied zu Nachrichtentexten im Abschnitt 1) die Darstellung in den Attributen **article_html** und **infobox_html** reichhaltiger. Darüber hinaus gibt es Erweiterungen bei den kategorischen Metadaten.

Zu den zusätzlichen Inhalten zählen:

* Social Media-Inhalte wie Tweets und YouTube-Videos
* Interaktiven Charts, Grafiken, Tabellen auf Basis von Datawrapper
* Weblines-Addons
* Im Text positionierte Bilder
* Interaktive Erklärgrafiken

Die Artikel enthalten Hinweise, an welcher Stelle des Textes welches Element sinnvoll eingebettet werden kann.

Darüber hinaus enthalten die Artikel - wie normale Nachrichtenartikel auch einige
HTML-Tags, z.B. Zwischenüberschriften und Listen.

Im Jahr 2021 hat die dpa-infocom GmbH, die Produktion in den dpa-Weblines,
den dpa-Faktenchecks und den dpa-Themenwelten auf multimediale Stories umgestellt.

<a href="/img/embed-grafik.png" target="_blank"><img src="/img/embed-grafik.png" width=100%></a>

### Eingebettete Inhalte via Web Components

Um in Topicles reichhaltige Inhalte zu transportieren, werden sogenannte Web
Components verwendet.

Web Components sind ein Internet-Standard, der von [allen aktuellen Browsern implementiert wird](https://caniuse.com/?search=web%20components) implementiert wird.
Sie bieten eine Reihe von Vorteilen. Die Wichtigsten sind:

- W3C-Standard - Web Components sind ein Internet-Standard des W3C. Dies bedeutet, dass die Technologie von allen aktuellen Browsern implementiert wird und mit großer Wahrscheinlichkeit auch auf lange Sicht unterstützt wird.
- Eine einfache Syntax, die sich nahtlos in HTML5 integriert. Jedes Tag, das das Zeichen -enthält, wird von HTML5-Parser / Browsern als Web Component interpretiert. Die innerhalb der Topicles verwendeten Web Components haben den Präfix dnl-, zum Beispiel “dnl-twitterembed” für einen Tweet.
- Datenschutzfreundlich - Sofern nicht zusätzlich Javascript ausgeführt wird, werden Web Components von Browsern ignoriert, auch die evtl. in Attributen enthaltene Links werden nicht aufgerufen. Dadurch können Sie garantieren, dass z.B.  wie Tweets und YouTube-Videos erst angezeigt werden, wenn alle Datenschutz-Vorkehrungen getroffen sind, also z.B. ihre Leser/innen der Verwendung externer Inhalte zugestmmt haben.

Dies bringt ihnen maximale Flexibilität für die Entscheidung, wie reichhaltige Artikel verarbeitet oder für ihre Leser/innen dargestellt werden.

Sie können:
- Web Components schon beim Import in ihr CMS umwandeln und in ihre Komponententechnologie übersetzen. (**empfohlenes Vorgehen**)
- Web Components unverändert bis in die App oder auf die Webseite durchreichen und sie dort mit Hilfe von entsprechendem Code entfalten.

Eine Referenzimplementierung für die einzelnen Web Components des dpa-newslabs ist auf [Github als open-source veröffentlicht](https://github.com/dpa-newslab/dpa-digitalwires-embeds).
Auf Basis dieser Referenzimplementierung oder eigener bereits existierender Web Components können Kunden dann auch z.B. eine Integration z.B der eingebetteten Inhalte in ihre CMP realisieren (d.h. Opt-In, Two-Click, Opt-Out etc.)

### Aktuell implementierte Web Components

Die folgenden Web Components sind aktuell implementiert und für die Nutzung
durch Redaktionen der dpa-Weblines, der dpa-Themenwelten und der dpa-Faktenchecks
freigegeben. Dies bedeutet nicht, dass sie bereits großflächig verwendet werden.

#### Twitter Embeds: dnl-twitterembed

Ein Tweet wird über die Komponente **dnl-twitterembed** eingebettet.
Diese hat ein einziges Attribut **src**, in dem die URL zum Tweet transportiert wird.

Komponente: **dnl-twitterembed**

- **src**: Link zum Tweet

\`\`\` html
<dnl-twitterembed src="https://twitter.com/gautamkhimlal/status/1168582968098488320"></dnl-twitterembed>
\`\`\`

Die Referenzimplementierung versucht, die trackingfreie Variante des Tweets zu laden.

#### YouTube Embeds: dnl-youtubeembed

Ein YouTube-Video wird über die Komponente **dnl-youtubeembed** eingebettet.
Diese hat ein einziges Attribut **src**, in dem die URL zum Video transportiert wird.

Komponente: **dnl-youtubeembed**

- **src**: Link zum Youtube-Video

\`\`\` html
<dnl-youtubeembed src="https://www.youtube-nocookie.com/embed/R3VMW6fxK6Y"></dnl-youtubeembed>
\`\`\`

Im Attribut **src** wird der von der Redaktion ausgewählte Link zum YouTube-Video transportiert.
In der Regel sollte dies ein Link zur Cookie-freien Variante sein.
Das kann aber nicht garantiert werden.

#### Datawrapper Embeds: dnl-dwchart

Von der dpa / dpa-infocom GmbH werden regelmäßig interaktive Visualisierungen mit
Hilfe der Tools des darauf spezialisierten deutschen Anbieters Datawrapper erstellt.
Diese lassen sich grob in die Kategorien: Chart, Karte und Tabelle einteilen.

Alle drei Typen werden mit Hilfe der Komponente **dnl-dwchart** eingebettet.
Aktuell hat diese Komponente ein Attribut **src**, das den Link zur Visualisierung enthält.

Komponente: **dnl-dwchart**

- **src**: Link zur Datawrapper-Visualisierung

\`\`\` html
<dnl-dwchart src="https://datawrapper.dwcdn.net/OEAsW/1"></dnl-dwchart>
\`\`\`

#### Interaktive Webgrafiken: dnl-wgchart

Von der dpa-infocom GmbH werden regelmäßig interaktive Erklärgrafiken erstellt.
Diese werden durch **dnl-wgchart** in einen Artikel eingebettet.

Komponente: **dnl-wgchart**

- **src**: Link zur Webgrafik

\`\`\` html
<dnl-wgchart src="https://webgrafik.dpa-addons.com/customers/client/aws/my/html/index.html?pathSrc=aktuell/2020_10_12/autoreifen/cont&amp;nameCSS=style_1_2.css&amp;versionScript=2.2"></dnl-wgchart>
\`\`\`

Um die Grafik responsiv darzustellen, gibt es zwei Möglichkeiten:

1. Rendering als \`iframe\` und eigene Implementierung der Logik zur (Re-)Skalierung via postMessage-Listener (vgl. <a href="https://github.com/dpa-newslab/dpa-digitalwires-embeds/blob/main/src/js/modules/DNLWebchart.js#L86" target="_blank">Referenzimplementierung</a>)
2. Sollten Sie bereits Webgrafiken aus dem dpa-Shop bezogen haben oder die \`dpa.js\` der dpa-infocom GmbH verwenden, empfehlen wir Ihnen die Verwendung der \`dpa.js\`: 

\`\`\` html
<div data-app="dpa-webgrafik" data-embed-type="pym" data-src="https://webgrafik.dpa-addons.com/customers/client/aws/my/html/index.html?pathSrc=aktuell/2020_10_12/autoreifen/cont&amp;nameCSS=style_1_2.css&amp;versionScript=2.2"></div>
<script type='text/javascript' src='https://core.dpa-infocom.net/js/dpa.js'></script>
\`\`\`

#### Positionierte Bilder: dnl-image

Die **dnl-image**-Komponente signalisiert die empfohlene Position eines Bildes im Artikel. Das **ref**-Attribut gibt an, welches der <NavLink exact to="/doc/format#assoziierte-bilder">assozierten Bilder</a> beim Import ins CMS oder auf die Website an dieser Stelle sinnvoll eingefügt werden kann. 

Empfohlenes Vorgehen:

1. Assoziierte Bilder ins CMS importieren
2. Artikel-HTML auf \`<dnl-image>\`-Elemente durchsuchen
3. \`<dnl-image>\`-Komponenten durch ins CMS-importierte Variante des jeweils referenzierten Bildes ersetzen

Komponente: **dnl-image**

- **ref**: URN des positionierten Bildes. Dient zum Finden des assozierten Bildes.
- **caption**: Bildunterschrift bei Verwendung des Bildes an der angegebenen Position.
- **creditline**: Die Creditline des Bildes.

\`\`\` html
<dnl-image ref="urn:newsml:dpa.com:20090101:201109-99-266444" caption="Gef\u00e4hrliche Mission: Khim Lal Gautam beim Sammeln von Daten am Mount Everest" creditline="Tshiring Jangbu Sherp/privat/dpa"></dnl-image>
\`\`\`

## Spezifische Erweiterungen und Anpassungen

### dpa-Weblines

#### Kurzcharakterisierung
dpa-Weblines sind Consumer-Ready-Inhalte, die für die automatisierte Publikation
im Inter-, Intra- oder Extranet sowie in Apps etc. freigegeben sind.

Gerne werden sie auch von Redaktionen als vorgefertigte, online-taugliche Multimediainhalte
verwendet, die dann als "Easy-to-Publish" Inhalte in das CMS System importiert und
von der Redaktion mit minimalem Aufwand regionalisiert oder anderweitig angepasst werden.

Meldungen der Weblines werden **zusätzlich rubriziert**, d.h in einen sogenannten
"Rubrikenbaum" von ca. 200 Rubriken einsortiert. Dabei kann eine Meldung mehreren
Rubriken zugeordnet sein, z.B. "Sport" und "Topthemen / Hintergründe".

Weblines sind in der Regel bebildert, in neuen nachrichtlichen Lagen kann es aber
ausnahmsweise vorkommen, dass ein Artikel erst in einer zweiten Version, sobald Bilder
verfügbar sind, mit Bildmaterial aktualisiert wird.

#### Webline-Erweiterung Rubrizierung: **dnltype:rubric**

**dnltype:rubric** ist die Weblines Rubrizierung. Hier wird der zusätzliche Key **is_current**
verwendet, um zu signalisieren, dass die Meldung aktuell noch diese Rubrizierung trägt.
Durch das Nachrichtenmanagement in den Weblines werden Rubrizierungen (insbesondere in "Topthemen")
entfernt, wenn die Meldung aus Sicht der Redaktion nicht mehr aktuell relevant ist.

Auch die Menge der Rubriken ist ein geschlossenes Vokabular:

- **dnltype:rubric**: Eine dpa-Weblines respektive dpa-News-Rubrik. Ein "Closed Vocabulary" von ca. 400 Begriffen.
  - [Aktuelle Version der dpa-Rubriken als CSV Datei](/res/qcodes_rubric.csv)

Beispiel:
\`\`\` javascript
{
  "type": "dnltype:rubric",
  "name": "weblines.infoline_rs.wissenschaft",
  "qcode": "dnlpath:weblines.infoline_rs.wissenschaft",
  "is_current": true
}
\`\`\`

#### Webline-Erweiterung Publizierbarer Berichtigungshinweis: **dpaednoterole:correction_consumerready**

Um Fehler transparent zu korrigieren, können (aber nicht müssen!) Weblines-Artikel mit \`signal\` (v.a. \`sig:correction\`) einen explizit zur Veröffentlichung gedachten Berichtigungshinweis enthalten. Dieser ist in der Liste der \`ednotes\` enthalten und hat das folgende Format:

\`\`\` javascript
{
  "role": "dpaednoterole:correction_consumerready",
  "ednote": "Die Stadt Lianjiang liegt in der Provinz Guangdong, nicht in der Provinz Guangzhou (10.07.2023, 15.08 Uhr)",
  "is_publishable": true
}
\`\`\`

Dieser Hinweis kann auf Websites zum entsprechenden Artikel gestellt und so auch Leser/innen zugänglich gemacht werden. Wir empfehlen, vor den Text der \`ednote\` einen kurzen Quellenhinweis zu schreiben (z.B. "Hinweis der dpa-Redaktion:", "Anmerkung d. Redaktion:").

#### Webline-Erweiterung Geografische Relevanz: **dnltype:scope**

**dnltype:scope** ist ein geographischer Bereich Deutschlands, für den eine Meldung
als relevant betrachtet wird.

Diese Relevanz erlaubt es den Empfängern des Topicles, den Inhalt Lokal- oder Regionalausgaben oder entsprechenden Verticals zuzuordnen.

Der Wert des QCodes des Typs **dnltype:scope** enthält als ID den 8-stelligen
"Allgemeinen Gemeindeschlüssel" (AGS). Es wird der QCode der kleinsten jeweils ausgewählten Verwaltungseinheit transportiert. Um die IDs höherer Verwaltungseinheiten abzuleiten, verwenden Sie die hierarchische Schlüsselstruktur des AGS.

Typischerweise werden von der dpa-Weblines Redaktion mindestens Kreise oder Kreisfreie
Städte als Scope ausgewählt.

Beispiel:
\`\`\` javascript
{
  "type": "dnltype:scope", 
  "name": "Flensburg", 
  "qcode": "dpax-kgs8:01001000"
}
\`\`\`

#### Beispielmeldungen

- <NavLink exact to="/doc/format#weblines-text-bild-artikel">Text-Bild-Artikel</NavLink>
- <NavLink exact to="/doc/format#weblines-multimediale-story">Multimediale-Story mit Embeds</NavLink>

### dpa-Textfunk und weitere Textdienste

#### Kurzcharakterisierung
Unter dem Begriff dpa-Textfunk werden die Textdienste der dpa-Redaktion zusammengefasst.
Neben dem dpa-Basisdienst umfasst er die dpa-Landesdienste, den dpa-Themendienst,
die Nachrichten für Kinder, sowie entsprechende Auslandsdienste.

#### Dienste weiterer Agenturen

Auch die Textdienste von dpa-AFX und news aktuell sind als Topicles via dpa-digitalwires erhältlich.

Für Pilotkunden stehen nach Absprache mit der KNA (Katholischen Nachrichten-Agentur) auch Text- und Bildfunkinhalte der KNA zur Verfügung. Sprechen Sie uns ggf. an. Dies gilt auch, wenn sie gerne Textdienste weiterer Nachrichtenagenturen via dpa-digitalwires erhalten möchten. Wir bemühen uns dann im Zusammenspiel mit der jeweiligen Agentur um eine Lösung.

#### Bisherige Bereitstellung

Bisher werden diese Dienste via Satellit oder FTP in den Formaten IPTC7901 oder
NewsML-G2 ausgeliefert.

In den dpa-digitalwires wurden bis Juni 2023 schon alle im IPTC7901 Format enthaltenen Informationen transportiert. Gleichzeitig wurde der eigentliche Text aber nicht mehr als reiner Text (in einem eingeschränkten Zeichensatz und einer Zeilenlänge von 69 Zeichen), sondern als HTML5-Markup übermittelt. Dies hat die Übernahme in digitale Systeme bereits erheblich erleichtert.

Seit Ende Juni 2023 werden auch alle relevanten Informationen aus dem NewsML G2
Format transportiert. Zusätzlich enthalten sind nun insbesondere die
Nutzungshinweise (\`usageTerms\`) die redaktionellen Hinweise (\`ednote\`) und
(\`genre\`) und \`description\`-Tags in der Form wie sie weiter oben
dokumentiert sind.

Damit steht einer Ablösung des Altformates durch die dpa-digitalwires nichts mehr im Wege.

#### Textfunk-Erweiterung Notizblock: **notepad**

Im Notizblock werden zusätzliche Informationen transportiert, die einer Redaktion
bei der Recherche helfen, oder weiterführende Informationen darstellen.

Es wird zwischen einem öffentlichen und einem nichtöffentlichen Teil des Notizblockes unterschieden.

Der öffentiche Teil findet sich unter **public_html**, der nicht zur Veröffentlichung
bestimmte unter **nonpublic_html**. Im Key **header_html** wird der Header des Notizblockes
transportiert.

Ein Teil der Informationen des öffentlichen Teiles des Notizblockes werden bereits
im Kern des Formates ausgeliefert: In **linkbox_html** finden Sie die im Bereich Internet
genannten Links als HTML. Viele weitere Bestandteile befinden sich in der \`ednotes\`-Liste.

Der nichtöffentliche Teil des Notizblockes darf nur dann weitergegeben werden,
wenn sichergestellt ist, dass die bereitgestellten Inhalte nicht automatisch
publiziert werden.
Bei den Consumer-Ready-Produkten wie z.B. dpa-Weblines können ansonsten nicht
zur Veröffentlichung bestimmte Informationen in diesem Fall zugänglich gemacht
werden.

\`\`\`javascript
"notepad": {
      "header_html": "<header><h3>Notizblock</h3></header>",
      "nonpublic_html": "<section class=\\"np-nonpublic\\">
<p class=\\"np-constraint\\"Die folgenden Informationen sind nicht zur Ver\u00f6ffentlichung bestimmt</p>
<h4>Kontakte</h4>
<ul>
<li>Autoren: Michel Winde (Br\u00fcssel), +32\u00a02\u00a0234\u00a06466, &lt;<a href=\\"mailto:winde.michel@dpa.com\\">winde.michel@dpa.com</a>&gt;, Martina Herzog (Berlin)</li>
<li>Redaktion: Hans-Hermann Nikolei (Berlin), +49 30 285231302, &lt;<a href=\\"mailto:politik-ausland@dpa.com\\">politik-ausland@dpa.com</a>&gt;</li>
<li>Foto: Newsdesk, +49 30 2852 31515, &lt;<a href=\\"mailto:foto@dpa.com\\">foto@dpa.com</a>&gt;</li></ul></section>",
      "public_html": null
}
\`\`\`

#### Beispielmeldungen 

- <NavLink exact to="/doc/format#textfunkmeldung">Meldung</NavLink>
- <NavLink exact to="/doc/format#textfunk-zusammenfassung">Zusammenfassung</NavLink>
- <NavLink exact to="/doc/format#textfunk-nachrichtenüberblick">Nachrichtenüberblick</NavLink>
- <NavLink exact to="/doc/format#textfunk-berichtigung">Berichtigung</NavLink>
- <NavLink exact to="/doc/format#textfunk-zurückziehung">Zurückziehung</NavLink>

### dpa-Bildfunk und weitere Bilderdienste

#### Kurzcharakterisierung

Unter dem Begriff dpa-Bildfunk werden die Bilderdienste der dpa-Redaktion zusammengefasst.
Neben dem dpa-Bilderdienst umfasst das dpa-Zentralbild, den Bilderdienst des Themendienstes
und der Nachrichten für Kinder, sowie entsprechende Auslandsdienste.

#### Dienste weiterer Agenturen

Auch das AP European Photo Wire und der Originalbildservice der news aktuell sind als Topicles via dpa-digitalwires erhältlich. 

Für Pilotkunden stehen nach Absprache mit der KNA (Katholischen Nachrichten-Agentur) auch Bildfunkdienste der KNA zur Verfügung. Sprechen sie uns ggf. an.

Dies gilt auch, wenn sie gerne Bilderdienste weiterer Nachrichtenagenturen via dpa-digitalwires erhalten möchten. Wir bemühen uns dann im Zusammenspiel mit der jeweiligen Agentur um eine Lösung.

#### Bisherige Belieferungsformate

Traditionell werden diese Dienste via Satellit oder FTP in den Formaten NewsML-G2 oder als reine Binärobjekte (JPEG) bereitgestellt.

In den dpa-digitalwires werden Bilder des dpa-Bildfunkes als Topicle mit einem leeren \`article_html\` und einer \`association\` auf eine oder mehrere \`renditions\` des Bildes ausgeliefert.
Die Metadaten des Bildes werden als Metadaten des Topicles transportiert.

#### Bildfunk-Erweiterung Bildbeschreibungen: **descriptions**, **caption**

Zusätzlich zur \`caption\` im \`associations\`-Teil eines Bildes, steht die Beschreibung auch in der \`descriptions\`-Liste. Hier finden sich meist zwei Varianten, die durch die \`role\` unterschieden werden können:
- \`drol:caption\`: Bildunterschrift des Bildes (meist identisch mit \`caption\`).
- \`dparol:caption-extended\`: Erweiterte Beschreibung. Die Bildunterschrift erweitert um rechtliche und ggf. redaktionelle Informationen und den Credit.

#### dpa-Bildfunk-Genres

Mit dem Typ \`dnltype:genre\` werden Informationen zum journalistischen Typ des Bildes übermittelt. Bei dpa-Bildern umfasst dies vor allem:

- \`dpascn\`: Klassifizierung der Bildszene.
- \`dpaimagekind\`: Klassifikation des Bildes (u.a. Aktuell, Archiv, Handout).
  - [Aktuelle Version der dpa-Bildklassifikationen als CSV Datei](/res/dpaimagekind.csv)

#### Bildfunk-Erweiterung **locationcreated**

Mit dem Typ \`dnltype:locationcreated\` werden Informationen zum Aufnahmeort des Bildes übermittelt. Die Struktur ist:

- **name**: Name des Aufnahmeorts.
- **geojson**: GeoJSON mit weiteren Details zum Aufnahmeort. Ggf. nur mit \`properties\` und ohne \`geometry\`.

#### Bildfunk-Erweiterung **infosource**

Via \`dnltype:infosource\` werden Details zur Herkunft eines Bildes übermittelt. Informationen zur Rolle einer \`infosource\`, zeigt ggf. der \`role\`-Key an. Bei dpa-Bildern gibt es:

- \`isrol:contentSource\`: Quelle des Bildes.
- \`dpaisrol:supplier\`: Weitere Glieder in der Verbreitungskette (wie z.B. weitere Agenturen etc.).

#### Hinweis

Eine zur Präsentation geeignete Form der gesamten Verbreiterkette findet sich
in der \`creditline\` der \`association\` respektive bei positionierten Bildern im
entsprechenden Attribut der Web Component.

### dpa-Themenwelten

#### Kurzcharakterisierung

Die dpa-Themenwelten sind Consumer-Ready-Inhalte, die für die automatisierte Publikation im Inter-, Intra- oder Extranet sowie in Apps etc. freigegeben sind. Die Artikel sind Evergreen-Content. Ihr Lebenszyklus ist auf eine möglichst lange Publikationsdauer ausgelegt, worin sie sich von den vorwiegend tagesaktuellen Inhalten der dpa-Weblines unterscheiden. Das bringt redaktionell und technisch einige Besonderheiten hinsichtlich der Auslieferung als auch der Aktualisierung mit sich.

Im Folgenden sind vor allem die technischen Metadaten dokumentiert. Für Empfehlungen zur technischen und inhaltlichen Umsetzung beachten Sie bitte auch die <a href="https://service.dpa-infocom.de/de/support/solutions/articles/202000031079-dpa-themenwelten-aus-technischer-sicht" target="_blank">Überblicksseite "dpa-Themenwelten aus technischer Sicht"</a>.

#### Themenwelten-Erweiterung dpaflextag:pc.topic

Die Zuordnung eines Artikels zu einer konkreten Themenwelt erfolgt über ein Flextag,
das in den \`categories\` repräsentiert wird und folgende Keys enthält:

- **type**: Der Typ der Kategorie, immer \`dnltype:flextag\`.
- **name**: Der Name der Themenwelt, kann beispielsweise auf einer Themenseite angezeigt werden.
- **qcode**: QCode der Themenwelt im Format \`dpaflextag:pc.topic.<kennung>\`. Die Kennung (z.B. \`laufen\` oder \`rueckenschmerzen\`) kann als URL-Slug verwendet werden.

Beispiel:
\`\`\`javascript
{
  "type": "dnltype:flextag",
  "name": "Wein",
  "qcode": "dpaflextag:pc.topic.wein"
}
\`\`\`

#### Themenwelten-Erweiterung dpaflextag:pc.lifecycle

Einige Themenwelt-Artikel behandeln Themen, die nicht mehr aktuell sind (z.B. Corona) oder stellen sich als nicht so klickträchtig heraus, wie zunächst gedacht. In solchen Fällen werden Artikel nicht mehr weiter gepflegt. Dies zeigt das Flextag \`dpaflextag:pc.lifecycle.archived\` an:

\`\`\`javascript
{
    "type": "dnltype:flextag",
    "name": "archiviert",
    "qcode": "dpaflextag:pc.lifecycle.archived"
}
\`\`\`

In der \`ednotes\`-Liste finden sich in diesen Fällen für Menschen gedachte Hinweise auf die Archivierung mit \`dpaednoterole:correction\` und \`dpaednoterole:correctionshort\`:

\`\`\`
"ednotes": [{
    "role": "dpaednoterole:correction",
    "ednote": "Dieser Themenwelten-Artikel wird seit 16. Oktober 2023 nicht mehr weiter aktualisiert.",
    "is_publishable": true
}, {
    "role": "dpaednoterole:correctionshort",
    "ednote": "Dieser Themenwelten-Artikel wird seit 16. Oktober 2023 nicht mehr weiter aktualisiert.",
    "is_publishable": true
}, ...]
\`\`\`

Detaillierte Hinweise zum Umgang mit Themenwelt-Archivierungen finden Sie auf <a href="https://service.dpa-infocom.de/de/support/solutions/articles/202000031079-dpa-themenwelten-aus-technischer-sicht#Archivierung-von-Themenwelt-Artikeln" target="_blank">https://service.dpa-infocom.de/de/support/solutions/articles/202000031079-dpa-themenwelten-aus-technischer-sicht#Archivierung-von-Themenwelt-Artikeln</a>.

#### Themenwelten-Erweiterung dpaflextag:userneed 

Ab Mai 2025 enthalten Themenwelten-Artikel Informationen darüber, auf welches Nutzungsbedürfnis (User Need) sie abgestimmt sind. Details zum verwendeten Modell finden sie im [dpa Innovation Blog](https://innovation.dpa.com/2024/04/04/user-needs-verdoppeln-leser-engagement-und-steigern-abo-rate/).

Das Metadatum ist Teil der \`categories\` und enthält folgende Keys:

- **type**: Der Typ der Kategorie, immer \`dnltype:flextag\`.
- **name**: Der Displayname der Kategorie.
- **qcode**: QCode der Kategorie.

Folgende QCodes sind möglich:
- **dpaflextag:userneed.updateme**
- **dpaflextag:userneed.educateme**
- **dpaflextag:userneed.givemeperspective**
- **dpaflextag:userneed.helpme**
- **dpaflextag:userneed.divertme**
- **dpaflextag:userneed.inspireme**

#### Veraltete Themenwelten-Erweiterungen

Metadaten der dpa-Themenwelten werden fortlaufend optimiert. Dabei können neue Metadaten hinzukommen und andere entfernt werden. 

Folgende früher vergebene Metadaten-Typen sind inzwischen veraltet: 
- Typ \`dnltype:publishadvice\`
  - qcode-Präfix \`dpa-limbic-system:*\`
  - qcode-Präfix \`advice-importance:*\`
  - qcode-Präfix \`advice-lifetime:*\`
- Typ \`dnltype:flextag\`
  - qcode-Präfix \`pc.evergreen.*\`

### Multimediale- und Featuredienste der dpa

#### Kurzcharakterisierung

Topicles in multimedialen und Feature-Diensten enthalten neben dem Text Verweise
auf zugehörige Bilder als Associations. Strukturell sind sie identisch zu Topicles im Abschnitt <NavLink exact to="/doc/format#dpa-weblines">dpa-Weblines</NavLink>.

Im Gegensatz zu den Inhalten in den Consumer-Ready-Produkten der dpa (wie den dpa-Weblines) sind diese Dienste in der Regel nicht zur automatischen Publikation freigegeben, sondern müssen durch eine Redaktion kuratiert/bearbeitet werden, bevor sie publiziert werden können.

Diese Dienste werden im Wesentlichen von den fremdsprachigen Redaktionen der dpa
angeboten, aber auch der AP Weltnachrichtendienst ist, als multimedialer Dienst, erhältlich.

#### Unterschied: Featuredienst / Multimedialer Dienst

Aktuell werden von der dpa im Wesentlichen die Fremdsprachendienste als multimediale Dienste produziert. Diese unterscheiden sich im Grad ihrer Bebilderung. Während bei den fremdsprachigen "Consumer News" jede Meldung bebildert ist, ist dies bei den nachrichtliche Diensten nur zu einem geweissen Grad der Fall. Oft wird die Bebilderung in folgenden Versionen eines Topicles nachgeliefert und ergänzt.

#### Dienste weiterer Agenturen

In den dpa-digitalwires steht auch der englischsprachige AP European News Service und die KNA-Webline als multimedialer Dienst zur Verfügung.

#### Ausblick

In den traditionellen Formaten IPTC7901 und NewsML-G2 ist die Auslieferung von multimedialen Diensten nicht möglich. 

Daher werden die für 2024 geplanten multimedialen Basis- und Landesdienste nur über die dpa-digitalwires als multimedialer Dienst verfügbar sein. 

Für einen Übergangszeitraum werden sie noch als monomediale Textdienste zur Verfügung gestellt werden.

#### Beispielmeldungen

- <NavLink exact to="/doc/format#englischer-multimediadienst">Englischer Multimediadienst</NavLink>
- <NavLink exact to="/doc/format#ap-european-news-service">AP European News Service</NavLink>

### Anhang Beispielmeldungen

- <NavLink exact to="/doc/format#multimedialer-basisdienst">Multimedialer Basisdienst</NavLink>
- <NavLink exact to="/doc/format#textfunkmeldung">Textfunkmeldung</NavLink>
- <NavLink exact to="/doc/format#textfunk-zusammenfassung">Textfunk Zusammenfassung</NavLink>
- <NavLink exact to="/doc/format#textfunk-nachrichtenüberblick">Textfunk Nachrichtenüberblick</NavLink>
- <NavLink exact to="/doc/format#textfunk-berichtigung">Textfunk Berichtigung</NavLink>
- <NavLink exact to="/doc/format#textfunk-zurückziehung">Textfunk Zurückziehung</NavLink>
- <NavLink exact to="/doc/format#bildfunk-bild">Bildfunk Bild</NavLink>
- <NavLink exact to="/doc/format#weblines-text-bild-artikel">Weblines Text-Bild-Artikel</NavLink>
- <NavLink exact to="/doc/format#weblines-multimediale-story">Weblines Multimediale Story</NavLink>
- <NavLink exact to="/doc/format#englischer-multimediadienst">Englischer Multimediadienst</NavLink>
- <NavLink exact to="/doc/format#ap-european-news-service">AP European News Service</NavLink>
- <NavLink exact to="/doc/format#grafik">Grafik</NavLink>
- <NavLink exact to="/doc/format#audio">Audio</NavLink>
- <NavLink exact to="/doc/format#video">Video</NavLink>
- <NavLink exact to="/doc/format#einfache-beispielmeldung-mit-allen-genutzen-html-tags">Einfache Beispielmeldung mit allen genutzen HTML-Tags</NavLink>

#### Multimedialer Basisdienst

<codeblockbrowser to='/res/bdt-multimedial.json'></codeblockbrowser>

#### Textfunkmeldung

<codeblockbrowser to='/res/textfunk-meldung.json'></codeblockbrowser>

#### Textfunk Zusammenfassung

<codeblockbrowser to='/res/textfunk-zusammenfassung.json'></codeblockbrowser>

#### Textfunk Nachrichtenüberblick

<codeblockbrowser to='/res/textfunk-nachrichtenueberblick.json'></codeblockbrowser>

#### Textfunk Berichtigung

<codeblockbrowser to='/res/berichtigung.json'></codeblockbrowser>

#### Textfunk Zurückziehung

<codeblockbrowser to='/res/kill.json'></codeblockbrowser>

#### Bildfunk Bild

<codeblockbrowser to='/res/bildfunk-bild.json'></codeblockbrowser>

#### Weblines Text-Bild-Artikel

<codeblockbrowser to='/res/weblines-text-bild-artikel.json'></codeblockbrowser>

#### Weblines multimediale Story

<codeblockbrowser to='/res/weblines-multimediale-story.json'></codeblockbrowser>

#### Englischer Multimediadienst

<codeblockbrowser to='/res/englischer-multimediadienst.json'></codeblockbrowser>

#### AP European News Service

<codeblockbrowser to='/res/ap-weltnachrichten.json'></codeblockbrowser>

#### Grafik

<codeblockbrowser to='/res/grafik.json'></codeblockbrowser>

#### Audio

<codeblockbrowser to='/res/audio.json'></codeblockbrowser>

#### Video

<codeblockbrowser to='/res/video_vtt.json'></codeblockbrowser>

#### Einfache Beispielmeldung mit allen genutzen HTML-Tags

<codeblockbrowser to='/res/beispielmeldung.json'></codeblockbrowser>
`

export { format }


// WEBPACK FOOTER //
// ./src/all_cards/docu/format.js